import { Button, DateTimePicker } from '@/components/common';
import { GroupDTO, InstitutionDTO, LabDTO, RfidTagStatusFilter } from '@/models';
import { RfidTagFilters } from '@/hooks/useRfidTagManagement';
import React, { useState } from 'react';

export interface RfidTagFiltersBarProps {
  filters: RfidTagFilters;
  labs: LabDTO[];
  groups: GroupDTO[];
  institutions: InstitutionDTO[];
  canChooseInstitution: boolean;
  hasActiveFilters: boolean;
  onChange: <K extends keyof RfidTagFilters>(key: K, value: RfidTagFilters[K]) => void;
  onReset: () => void;
}

const STATUS_OPTIONS: ReadonlyArray<{ value: RfidTagStatusFilter; label: string }> = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
  { value: 'Expired', label: 'Expired' },
  { value: 'Unassigned', label: 'Unassigned' },
];

function toDateOnly(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseDateOnly(s: string): Date | null {
  if (!s) return null;
  // Parse as local midnight to match the picker's expectation.
  const parts = s.split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return null;
  const [y, m, d] = parts;
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
}

export const RfidTagFiltersBar: React.FC<RfidTagFiltersBarProps> = ({
  filters,
  institutions,
  canChooseInstitution,
  hasActiveFilters,
  onChange,
  onReset,
}) => {


  const expiresBeforeDate = parseDateOnly(filters.expiresBefore);
  // Show Advanced open by default if user already has an advanced filter active.
  const [showAdvanced, setShowAdvanced] = useState<boolean>(filters.assigned !== 'any');

  const handleExpiresChange = (date: Date | null) => {
    onChange('expiresBefore', date ? toDateOnly(date) : '');
  };

  return (
    <div className="card border-0 shadow-sm mb-3">
      <div className="card-body">
        <div className="row g-3 align-items-end">
          {canChooseInstitution && (
            <div className="col-md-3">
              <label className="form-label small text-muted mb-1" htmlFor="rfid-tag-institution">
                Institution
              </label>
              <select
                id="rfid-tag-institution"
                className="form-select form-select-sm"
                value={filters.institutionId}
                onChange={(e) => onChange('institutionId', e.target.value)}
              >
                <option value="">All institutions</option>
                {institutions.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name || i.id}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="col-md-3">
            <label className="form-label small text-muted mb-1" htmlFor="rfid-tag-status">
              Status
            </label>
            <select
              id="rfid-tag-status"
              className="form-select form-select-sm"
              value={filters.status}
              onChange={(e) => onChange('status', e.target.value as RfidTagStatusFilter | '')}
            >
              <option value="">Any</option>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label small text-muted mb-1" htmlFor="rfid-tag-expires-before">
              Expires on or before
            </label>
            <DateTimePicker
              id="rfid-tag-expires-before"
              size="sm"
              showTime={false}
              value={expiresBeforeDate}
              onChange={handleExpiresChange}
              placeholder="Pick a cutoff date"
            />
            <div className="d-flex flex-wrap gap-1 mt-1">
            </div>
          </div>

          <div className="col-md-3"></div>

          <div className="col-md-3">
            <label className="form-label small text-muted mb-1" htmlFor="rfid-tag-user-name">
              User name
            </label>
            <input
              id="rfid-tag-user-name"
              type="text"
              className="form-control form-control-sm"
              placeholder="e.g. jane"
              value={filters.userName}
              onChange={(e) => onChange('userName', e.target.value)}
            />
            <small className="text-muted">Matches assigned cards only</small>
          </div>

          <div className="col-md-3">
            <label className="form-label small text-muted mb-1" htmlFor="rfid-tag-user-email">
              User email
            </label>
            <input
              id="rfid-tag-user-email"
              type="text"
              className="form-control form-control-sm"
              placeholder="e.g. jane@acme"
              value={filters.userEmail}
              onChange={(e) => onChange('userEmail', e.target.value)}
            />
            <small className="text-muted">Matches assigned cards only</small>
          </div>
          <div className="col-md-3">
            <label className="form-label small text-muted mb-1" htmlFor="rfid-tag-serial">
              Serial
            </label>
            <input
              id="rfid-tag-serial"
              type="text"
              className="form-control form-control-sm"
              placeholder="Search serial..."
              value={filters.serialNumber}
              onChange={(e) => onChange('serialNumber', e.target.value)}
            />
          </div>

          <div className="col-md-12 d-flex justify-content-between align-items-center pt-2">
            <button
              type="button"
              className="btn btn-link btn-sm p-0"
              onClick={() => setShowAdvanced((v) => !v)}
              aria-expanded={showAdvanced}
            >
              <i className={`fas ${showAdvanced ? 'fa-caret-down' : 'fa-caret-right'} me-1`} />
              Advanced filters
            </button>
            <Button
              variant="secondary"
              size="sm"
              icon="fas fa-undo"
              onClick={onReset}
              disabled={!hasActiveFilters}
            >
              Reset filters
            </Button>
          </div>

          {showAdvanced && (
            <div className="col-md-6">
              <label className="form-label small text-muted mb-1">Assignment (raw)</label>
              <div
                className="btn-group btn-group-sm w-100"
                role="group"
                aria-label="Assignment filter"
              >
                <button
                  type="button"
                  className={`btn ${
                    filters.assigned === 'any' ? 'btn-primary' : 'btn-outline-secondary'
                  }`}
                  onClick={() => onChange('assigned', 'any')}
                >
                  Any
                </button>
                <button
                  type="button"
                  className={`btn ${
                    filters.assigned === 'assigned' ? 'btn-primary' : 'btn-outline-secondary'
                  }`}
                  onClick={() => onChange('assigned', 'assigned')}
                >
                  Assigned
                </button>
                <button
                  type="button"
                  className={`btn ${
                    filters.assigned === 'unassigned' ? 'btn-primary' : 'btn-outline-secondary'
                  }`}
                  onClick={() => onChange('assigned', 'unassigned')}
                >
                  Unassigned
                </button>
              </div>
              <small className="text-muted">
                Prefer the Status dropdown above; this is ANDed with Status on the server.
              </small>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RfidTagFiltersBar;
